import { memo, type FC, type PointerEvent } from "react";
import type { PlaneCoordinate, PlaneScreenPoint, ToScreenPoint } from "./CartesianPlane";
import styles from "./DeckSideHandlesLayer.module.css";

type DeckSideHandlesLayerProps = {
  // The finished shape's vertices: one handle per side, the closing one included.
  coordinates: PlaneCoordinate[];
  draggedSideIndex: number | null;
  onSideDrag: (event: PointerEvent<SVGLineElement>) => void;
  onSideDragEnd: (event: PointerEvent<SVGLineElement>) => void;
  onSideDragStart: (event: PointerEvent<SVGLineElement>, sideIndex: number) => void;
  toScreenPoint: ToScreenPoint;
};

type Side = {
  endPoint: PlaneScreenPoint;
  index: number;
  isHorizontal: boolean;
  startPoint: PlaneScreenPoint;
};

// A wide, invisible hit area along each side of the finished shape (highlighted on hover): dragging
// it moves that side.
const DeckSideHandlesLayer: FC<DeckSideHandlesLayerProps> = ({
  coordinates,
  draggedSideIndex,
  onSideDrag,
  onSideDragEnd,
  onSideDragStart,
  toScreenPoint,
}) => {
  const sides = coordinates
    .map((coordinate, index): Side | null => {
      const nextCoordinate = coordinates[(index + 1) % coordinates.length];
      const startPoint = toScreenPoint(coordinate, index);
      const endPoint = nextCoordinate ? toScreenPoint(nextCoordinate, index + 1) : null;

      if (!nextCoordinate || !startPoint || !endPoint) {
        return null;
      }

      return {
        endPoint,
        index,
        isHorizontal: coordinate.y === nextCoordinate.y,
        startPoint,
      };
    })
    .filter((side): side is Side => side !== null);

  if (sides.length === 0) {
    return null;
  }

  return (
    <svg className={styles.sideHandles} aria-hidden="true">
      {sides.map((side) => (
        <line
          key={side.index}
          className={`${styles.sideHandle} ${
            side.isHorizontal ? styles.horizontalSideHandle : styles.verticalSideHandle
          } ${draggedSideIndex === side.index ? styles.draggedSideHandle : ""}`}
          x1={side.startPoint.pixelX}
          y1={side.startPoint.pixelY}
          x2={side.endPoint.pixelX}
          y2={side.endPoint.pixelY}
          onPointerDown={(event) => onSideDragStart(event, side.index)}
          onPointerMove={onSideDrag}
          onPointerUp={onSideDragEnd}
          onPointerCancel={onSideDragEnd}
        />
      ))}
    </svg>
  );
};

// Memoized: re-renders only when the shape, the dragged side or the view change.
export default memo(DeckSideHandlesLayer);
